import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(20); // 10 per row × 2 rows
  const [sortConfig, setSortConfig] = useState({ key: 'createdDate', direction: 'desc' });
  const [expandedCards, setExpandedCards] = useState({});
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'verified', 'pending', 'free', 'essential', 'premium', 'oc', 'selective'
  const [newUserData, setNewUserData] = useState({
    email: '',
    parentName: '',
    studentName: '',
    parentEmail: '',
    phone: '',
    tempPassword: ''
  });

  useEffect(() => {
    checkAdminAccess();
  }, []);

  // Filter users based on search query and status filter
  useEffect(() => {
    let filtered = users;

    // Filter out admin user
    filtered = filtered.filter(user => user.email !== 'admin@prepsmart.com.au');

    // Apply status filter
    if (filterStatus === 'verified') {
      filtered = filtered.filter(user => user.status === 'CONFIRMED');
    } else if (filterStatus === 'pending') {
      filtered = filtered.filter(user => user.status === 'UNCONFIRMED');
    } else if (filterStatus === 'free') {
      filtered = filtered.filter(user => !user.premiumAccess || user.tier === 'free');
    } else if (filterStatus === 'essential') {
      filtered = filtered.filter(user => user.tier === 'essential');
    } else if (filterStatus === 'premium') {
      filtered = filtered.filter(user => user.tier === 'premium' || (user.premiumAccess && user.tier !== 'essential'));
    } else if (filterStatus === 'oc') {
      filtered = filtered.filter(user => user.examType === 'oc');
    } else if (filterStatus === 'selective') {
      filtered = filtered.filter(user => user.examType === 'selective');
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(query) ||
        user.studentName?.toLowerCase().includes(query) ||
        user.parentName?.toLowerCase().includes(query) ||
        user.parentEmail?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query)
      );
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, users, filterStatus]);

  // Sort users
  const sortUsers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });

    const sorted = [...filteredUsers].sort((a, b) => {
      let aVal = a[key] || '';
      let bVal = b[key] || '';
      
      if (key === 'createdDate') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredUsers(sorted);
  };

  const toggleCard = (username) => {
    setExpandedCards(prev => ({
      ...prev,
      [username]: !prev[username]
    }));
  };

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const checkAdminAccess = async () => {
    try {
      // Check if user is logged in and is admin
      const { fetchUserAttributes } = await import('aws-amplify/auth');
      const attributes = await fetchUserAttributes();
      
      // Check if user is the specific admin
      const role = attributes.email === 'admin@prepsmart.com.au' ? 'admin' : 'student';
      if (role !== 'admin') {
        setError('Access denied. Admin privileges required.');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      setCurrentUser({ attributes });
      await fetchUsers();
    } catch (err) {
      console.error('Auth check error:', err);
      setError('Please login to access admin panel');
      setTimeout(() => navigate('/login'), 2000);
    }
  };

  const fetchUsers = async () => {
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      
      // Get current session for auth token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      // Fetch users from API
      const response = await fetch(`${awsConfig.apiEndpoint}/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users || []);
      setFilteredUsers(data.users || []);
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Failed to load users. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
      localStorage.removeItem('user');
      navigate('/');
      window.location.reload();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleResendVerification = async (username, email) => {
    if (!window.confirm(`Manually verify ${email}? This will mark them as verified without email confirmation.`)) return;
    
    setActionLoading(prev => ({ ...prev, [username]: 'resend' }));
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to verify user');
      }

      alert(`${email} has been verified successfully!`);
      await fetchUsers();
    } catch (err) {
      console.error('Verify user error:', err);
      alert(err.message || 'Failed to verify user');
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }));
    }
  };

  const handleSetTempPassword = async (username, email) => {
    const tempPassword = prompt(`Enter temporary password for ${email}:\n\n(Min 8 chars, uppercase, lowercase, number)`);
    if (!tempPassword) return;
    
    if (tempPassword.length < 8) {
      alert('Password must be at least 8 characters long');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [username]: 'password' }));
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/set-temp-password`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, tempPassword })
      });

      if (!response.ok) throw new Error('Failed to set temporary password');

      alert(`Temporary password set for ${email}\n\nPassword: ${tempPassword}\n\nUser must change it on first login.`);
      await fetchUsers();
    } catch (err) {
      console.error('Set temp password error:', err);
      alert('Failed to set temporary password: ' + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }));
    }
  };

  const handleDeleteUser = async (username, email) => {
    if (!window.confirm(`Are you sure you want to DELETE ${email}?\n\nThis action cannot be undone!`)) return;
    
    setActionLoading(prev => ({ ...prev, [username]: 'delete' }));
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/delete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username })
      });

      if (!response.ok) throw new Error('Failed to delete user');

      alert(`User ${email} has been deleted successfully.`);
      await fetchUsers();
    } catch (err) {
      console.error('Delete user error:', err);
      alert('Failed to delete user: ' + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }));
    }
  };

  const handleTogglePremium = async (username, email, currentTier, newTier) => {
    const tierLabels = { 'false': 'Free', 'essential': 'Essential', 'premium': 'Premium' };
    if (!window.confirm(`Set ${email} to ${tierLabels[newTier] || newTier} tier?`)) return;
    
    setActionLoading(prev => ({ ...prev, [username]: 'premium' }));
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/toggle-premium`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, tier: newTier })
      });

      if (!response.ok) throw new Error('Failed to update tier');

      alert(`${email} updated to ${tierLabels[newTier]} tier`);
      await fetchUsers();
    } catch (err) {
      console.error('Toggle premium error:', err);
      alert('Failed to update tier: ' + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }));
    }
  };

  const handleResetExamYear = async (username, email, newYear) => {
    const yearText = newYear ? newYear : 'cleared';
    if (!window.confirm(`Set exam year to ${yearText} for ${email}?`)) return;
    
    setActionLoading(prev => ({ ...prev, [username]: 'examyear' }));
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/set-exam-year`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, examYear: newYear })
      });

      if (!response.ok) throw new Error('Failed to set exam year');

      alert(`Exam year ${newYear ? `set to ${newYear}` : 'cleared'} for ${email}.`);
      await fetchUsers();
    } catch (err) {
      console.error('Set exam year error:', err);
      alert('Failed to set exam year: ' + err.message);
    } finally {
      setActionLoading(prev => ({ ...prev, [username]: null }));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const { awsConfig } = await import('../aws-config');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${awsConfig.apiEndpoint}/users/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUserData)
      });

      if (!response.ok) throw new Error('Failed to create user');

      alert(`User created successfully!\n\nEmail: ${newUserData.email}\nPassword: ${newUserData.tempPassword}\n\nUser must change password on first login.`);
      setShowCreateModal(false);
      setNewUserData({ email: '', parentName: '', studentName: '', parentEmail: '', phone: '', tempPassword: '' });
      await fetchUsers();
    } catch (err) {
      console.error('Create user error:', err);
      alert('Failed to create user: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    try {
      // Prepare CSV data
      const headers = ['Login Email', 'Student Name', 'Parent Name', 'Parent Email', 'Phone', 'Exam Year', 'Exam Type', 'Status', 'Tier', 'Registered On'];
      const csvRows = [headers.join(',')];

      users.forEach(user => {
        const tierLabel = user.tier === 'premium' ? 'Premium' : user.tier === 'essential' ? 'Essential' : user.premiumAccess ? 'Premium' : 'Free';
        const row = [
          `"${user.email || ''}"`,
          `"${user.studentName || ''}"`,
          `"${user.parentName || ''}"`,
          `"${user.parentEmail || ''}"`,
          `"${user.phone || ''}"`,
          `"${user.examYear || 'Not set'}"`,
          `"${user.examType === 'oc' ? 'OC' : user.examType === 'selective' ? 'Selective' : 'Not set'}"`,
          `"${user.status || ''}"`,
          `"${tierLabel}"`,
          `"${user.createdDate ? new Date(user.createdDate).toLocaleDateString() : ''}"`
        ];
        csvRows.push(row.join(','));
      });

      // Create CSV blob
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      
      // Create download link
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `prepsmart-users-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`✅ Exported ${users.length} users to CSV file`);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data: ' + err.message);
    }
  };

  if (error && !currentUser) {
    return (
      <div className="card" style={{ maxWidth: '600px', margin: '60px auto', textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: '#ef4444', marginBottom: '15px' }}>Access Denied</h2>
        <p style={{ color: '#666' }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: '#f9fafb',
      width: '100%',
      overflowX: 'hidden'
    }}>
      <div style={{ 
        width: '100%', 
        margin: '0', 
        padding: '20px'
      }}>
      {/* Compact Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e5e7eb'
      }}>
        <div>
          <h1 style={{ fontSize: '28px', marginBottom: '5px', color: '#1f2937' }}>Admin Dashboard</h1>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>Manage users and monitor platform activity</p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            padding: '10px 20px',
            background: 'white',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            color: '#374151',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Logout
        </button>
      </div>

      {/* Compact Stats and Actions - Now Clickable */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '15px', marginBottom: '25px' }}>
        <div 
          onClick={() => setFilterStatus('all')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'all' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'all' ? '2px solid #4f46e5' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Total Users</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1f2937' }}>{users.length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('verified')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'verified' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'verified' ? '2px solid #10b981' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Verified</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#10b981' }}>{users.filter(u => u.status === 'CONFIRMED').length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('pending')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'pending' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'pending' ? '2px solid #f59e0b' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Pending</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#f59e0b' }}>{users.filter(u => u.status === 'UNCONFIRMED').length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('free')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'free' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'free' ? '2px solid #6b7280' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Free</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#6b7280' }}>{users.filter(u => !u.premiumAccess && u.tier !== 'essential' && u.tier !== 'premium').length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('essential')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'essential' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'essential' ? '2px solid #0ea5e9' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Essential</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#0ea5e9' }}>{users.filter(u => u.tier === 'essential').length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('premium')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'premium' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'premium' ? '2px solid #8b5cf6' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Premium</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#8b5cf6' }}>{users.filter(u => u.tier === 'premium' || (u.premiumAccess && u.tier !== 'essential')).length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('oc')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'oc' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'oc' ? '2px solid #0ea5e9' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>OC</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#0ea5e9' }}>{users.filter(u => u.examType === 'oc').length}</div>
        </div>
        <div 
          onClick={() => setFilterStatus('selective')}
          style={{ 
            padding: '20px', 
            background: filterStatus === 'selective' ? '#f3f4f6' : 'white', 
            border: filterStatus === 'selective' ? '2px solid #7c3aed' : '1px solid #e5e7eb', 
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '5px', textTransform: 'uppercase', fontWeight: '600' }}>Selective</div>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#7c3aed' }}>{users.filter(u => u.examType === 'selective').length}</div>
        </div>
      </div>

      {/* Actions Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setShowCreateModal(true)}
          style={{
            padding: '10px 16px',
            background: '#4f46e5',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Create User
        </button>
        <button
          onClick={() => fetchUsers()}
          style={{
            padding: '10px 16px',
            background: 'white',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Refresh
        </button>
        <button
          onClick={handleExportData}
          style={{
            padding: '10px 16px',
            background: 'white',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Export CSV
        </button>
      </div>

      {/* Users Table */}
      <div style={{ 
        background: 'white', 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px', 
        overflow: 'hidden',
        width: '100%'
      }}>
        {/* Search Bar and Sort */}
        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Search by email, name, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: '250px',
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <select
              value={sortConfig.key}
              onChange={(e) => sortUsers(e.target.value)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                background: 'white',
                cursor: 'pointer'
              }}
            >
              <option value="createdDate">Sort by: Registration Date</option>
              <option value="email">Sort by: Email</option>
              <option value="studentName">Sort by: Student Name</option>
              <option value="status">Sort by: Status</option>
            </select>
            <button
              onClick={() => sortUsers(sortConfig.key)}
              style={{
                padding: '10px 14px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              title={`Sort ${sortConfig.direction === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          {(searchQuery || filterStatus !== 'all') && (
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', fontSize: '13px', color: '#6b7280' }}>
              <span>Showing {filteredUsers.length} of {users.length} users</span>
              {filterStatus !== 'all' && (
                <span style={{ 
                  padding: '3px 8px', 
                  background: '#f3f4f6', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  Filter: {filterStatus === 'oc' ? 'OC' : filterStatus === 'selective' ? 'Selective' : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
                </span>
              )}
              {(searchQuery || filterStatus !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterStatus('all');
                  }}
                  style={{
                    padding: '3px 8px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div style={{ 
            padding: '20px', 
            background: '#fee2e2', 
            color: '#dc2626',
            margin: '20px'
          }}>
            {error}
          </div>
        ) : users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
            <p>No users registered yet</p>
          </div>
        ) : (
          <>
            {/* Card-based user list - 2 COLUMN GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', padding: '20px' }}>
              {currentUsers.map((user, index) => (
                <div key={index} style={{
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '14px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'all 0.2s',
                  cursor: 'default'
                }}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'}
                >
                  {/* Header with email and status - CLICKABLE & COMPACT */}
                  <div 
                    onClick={() => toggleCard(user.username)}
                    style={{ 
                      marginBottom: expandedCards[user.username] ? '12px' : '0', 
                      paddingBottom: expandedCards[user.username] ? '10px' : '0', 
                      borderBottom: expandedCards[user.username] ? '1px solid #f3f4f6' : 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                        <span style={{ fontSize: '12px', color: '#6b7280', transition: 'transform 0.2s', display: 'inline-block', transform: expandedCards[user.username] ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}>
                          ▶
                        </span>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.email}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0, marginLeft: '8px' }}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600',
                          background: 
                            user.status === 'CONFIRMED' ? '#d1fae5' : 
                            user.status === 'FORCE_CHANGE_PASSWORD' ? '#fef3c7' :
                            user.status === 'UNCONFIRMED' ? '#fee2e2' : '#e5e7eb',
                          color: 
                            user.status === 'CONFIRMED' ? '#065f46' : 
                            user.status === 'FORCE_CHANGE_PASSWORD' ? '#92400e' :
                            user.status === 'UNCONFIRMED' ? '#991b1b' : '#374151',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.status === 'CONFIRMED' ? '✓' : 
                           user.status === 'FORCE_CHANGE_PASSWORD' ? '⚠' :
                           user.status === 'UNCONFIRMED' ? '⏳' : user.status}
                        </span>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '10px',
                          fontWeight: '600',
                          background: user.tier === 'premium' || (user.premiumAccess && user.tier !== 'essential') ? '#ede9fe' : user.tier === 'essential' ? '#e0f2fe' : '#f3f4f6',
                          color: user.tier === 'premium' || (user.premiumAccess && user.tier !== 'essential') ? '#6b21a8' : user.tier === 'essential' ? '#0369a1' : '#6b7280',
                          whiteSpace: 'nowrap'
                        }}>
                          {user.tier === 'premium' || (user.premiumAccess && user.tier !== 'essential') ? '🚀' : user.tier === 'essential' ? '⭐' : 'Free'}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '20px' }}>
                      {user.createdDate ? new Date(user.createdDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </div>
                  </div>

                  {/* User details - Only show when expanded - COMPACT */}
                  {expandedCards[user.username] && (
                    <>
                      <div style={{ marginBottom: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '12px' }}>
                        <div>
                          <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '600' }}>Student</div>
                          <div style={{ color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.studentName || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '600' }}>Parent</div>
                          <div style={{ color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.parentName || '-'}</div>
                        </div>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '600' }}>Parent Email</div>
                          <div style={{ color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.parentEmail || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '600' }}>Phone</div>
                          <div style={{ color: '#1f2937' }}>{user.phone || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#9ca3af', fontSize: '10px', marginBottom: '2px', textTransform: 'uppercase', fontWeight: '600' }}>Exam</div>
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                            {user.examYear ? (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: '600',
                                background: user.examYear === '2026' ? '#fef3c7' : '#dbeafe',
                                color: user.examYear === '2026' ? '#92400e' : '#1e40af'
                              }}>
                                {user.examYear}
                              </span>
                            ) : (
                              <span style={{ color: '#9ca3af', fontSize: '10px' }}>-</span>
                            )}
                            {user.examType && (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '3px',
                                fontSize: '10px',
                                fontWeight: '600',
                                background: user.examType === 'oc' ? '#dbeafe' : '#f3e8ff',
                                color: user.examType === 'oc' ? '#1e40af' : '#7c3aed'
                              }}>
                                {user.examType === 'oc' ? 'OC' : 'SEL'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Action buttons - COMPACT */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', paddingTop: '10px', borderTop: '1px solid #f3f4f6' }}>
                    {user.status === 'UNCONFIRMED' && (
                      <button
                        onClick={() => handleResendVerification(user.username, user.email)}
                        disabled={actionLoading[user.username] === 'resend'}
                        title="Manually verify user"
                        style={{
                          padding: '4px 8px',
                          background: '#8b5cf6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: actionLoading[user.username] === 'resend' ? 'not-allowed' : 'pointer',
                          fontSize: '11px',
                          fontWeight: '500',
                          opacity: actionLoading[user.username] === 'resend' ? 0.6 : 1
                        }}
                      >
                        {actionLoading[user.username] === 'resend' ? '...' : 'Enable'}
                      </button>
                    )}
                    {(() => {
                      const currentTier = user.tier === 'premium' || (user.premiumAccess && user.tier !== 'essential') ? 'premium' : user.tier === 'essential' ? 'essential' : 'free';
                      return (
                        <>
                          <button
                            onClick={() => currentTier !== 'free' && handleTogglePremium(user.username, user.email, user.tier, 'false')}
                            disabled={currentTier === 'free' || actionLoading[user.username] === 'premium'}
                            title="Revoke premium access"
                            style={{
                              padding: '4px 8px',
                              background: currentTier === 'free' ? '#e5e7eb' : '#ef4444',
                              color: currentTier === 'free' ? '#9ca3af' : 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: currentTier === 'free' || actionLoading[user.username] === 'premium' ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              opacity: currentTier === 'free' ? 0.5 : actionLoading[user.username] === 'premium' ? 0.6 : 1
                            }}
                          >
                            {actionLoading[user.username] === 'premium' ? '...' : 'Revoke'}
                          </button>
                          <button
                            onClick={() => currentTier !== 'essential' && handleTogglePremium(user.username, user.email, user.tier, 'essential')}
                            disabled={currentTier === 'essential' || actionLoading[user.username] === 'premium'}
                            title={currentTier === 'essential' ? 'Currently Essential tier' : 'Set to Essential tier'}
                            style={{
                              padding: '4px 8px',
                              background: currentTier === 'essential' ? '#e0f2fe' : '#0ea5e9',
                              color: currentTier === 'essential' ? '#0369a1' : 'white',
                              border: currentTier === 'essential' ? '2px solid #0ea5e9' : 'none',
                              borderRadius: '4px',
                              cursor: currentTier === 'essential' || actionLoading[user.username] === 'premium' ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              opacity: actionLoading[user.username] === 'premium' ? 0.6 : 1
                            }}
                          >
                            {actionLoading[user.username] === 'premium' ? '...' : currentTier === 'essential' ? '✓ Ess' : '⭐'}
                          </button>
                          <button
                            onClick={() => currentTier !== 'premium' && handleTogglePremium(user.username, user.email, user.tier, 'premium')}
                            disabled={currentTier === 'premium' || actionLoading[user.username] === 'premium'}
                            title={currentTier === 'premium' ? 'Currently Premium tier' : 'Set to Premium tier'}
                            style={{
                              padding: '4px 8px',
                              background: currentTier === 'premium' ? '#ede9fe' : '#8b5cf6',
                              color: currentTier === 'premium' ? '#6b21a8' : 'white',
                              border: currentTier === 'premium' ? '2px solid #8b5cf6' : 'none',
                              borderRadius: '4px',
                              cursor: currentTier === 'premium' || actionLoading[user.username] === 'premium' ? 'not-allowed' : 'pointer',
                              fontSize: '11px',
                              fontWeight: '500',
                              opacity: actionLoading[user.username] === 'premium' ? 0.6 : 1
                            }}
                          >
                            {actionLoading[user.username] === 'premium' ? '...' : currentTier === 'premium' ? '✓ Pro' : '🚀'}
                          </button>
                        </>
                      );
                    })()}
                    <button
                      onClick={() => handleSetTempPassword(user.username, user.email)}
                      disabled={actionLoading[user.username] === 'password'}
                      title="Reset password"
                      style={{
                        padding: '4px 8px',
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: actionLoading[user.username] === 'password' ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: '500',
                        opacity: actionLoading[user.username] === 'password' ? 0.6 : 1
                      }}
                    >
                      {actionLoading[user.username] === 'password' ? '...' : '🔑'}
                    </button>
                    <button
                      onClick={() => {
                        const year = prompt(`Enter exam year for ${user.email} (or leave empty to clear):`);
                        if (year !== null) {
                          handleResetExamYear(user.username, user.email, year);
                        }
                      }}
                      disabled={actionLoading[user.username] === 'examYear'}
                      title="Set exam year"
                      style={{
                        padding: '4px 8px',
                        background: '#f59e0b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: actionLoading[user.username] === 'examYear' ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: '500',
                        opacity: actionLoading[user.username] === 'examYear' ? 0.6 : 1
                      }}
                    >
                      {actionLoading[user.username] === 'examYear' ? '...' : '📅'}
                    </button>
                    <button
                      onClick={() => window.open(`/dashboard?impersonate=${user.username}`, '_blank')}
                      title={`View dashboard as ${user.studentName || user.email}`}
                      style={{
                        padding: '4px 8px',
                        background: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: '500'
                      }}
                    >
                      👁️
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.username, user.email)}
                      disabled={actionLoading[user.username] === 'delete'}
                      title="Delete user"
                      style={{
                        padding: '4px 8px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: actionLoading[user.username] === 'delete' ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: '500',
                        opacity: actionLoading[user.username] === 'delete' ? 0.6 : 1
                      }}
                    >
                      {actionLoading[user.username] === 'delete' ? '...' : '🗑️'}
                    </button>
                  </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '20px', 
                borderTop: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>
                  Showing {indexOfFirstUser + 1} to {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: '6px 12px',
                      background: currentPage === 1 ? '#f3f4f6' : 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    Previous
                  </button>
                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => paginate(i + 1)}
                      style={{
                        padding: '6px 12px',
                        background: currentPage === i + 1 ? '#4f46e5' : 'white',
                        color: currentPage === i + 1 ? 'white' : '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '500'
                      }}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: '6px 12px',
                      background: currentPage === totalPages ? '#f3f4f6' : 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ 
            maxWidth: '600px', 
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            padding: '30px'
          }}>
            <h2 style={{ fontSize: '28px', marginBottom: '20px', color: '#4f46e5' }}>
              Create New User
            </h2>
            <form onSubmit={handleCreateUser}>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Parent/Guardian Name *
                </label>
                <input
                  type="text"
                  value={newUserData.parentName}
                  onChange={(e) => setNewUserData({...newUserData, parentName: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Student Name *
                </label>
                <input
                  type="text"
                  value={newUserData.studentName}
                  onChange={(e) => setNewUserData({...newUserData, studentName: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Parent/Guardian Email *
                </label>
                <input
                  type="email"
                  value={newUserData.parentEmail}
                  onChange={(e) => setNewUserData({...newUserData, parentEmail: e.target.value})}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  placeholder="parent@example.com"
                />
                <small style={{ color: '#666', fontSize: '14px' }}>
                  For updates and notifications (can be same as login email)
                </small>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  value={newUserData.phone}
                  onChange={(e) => setNewUserData({...newUserData, phone: e.target.value})}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  placeholder="+61..."
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Temporary Password *
                </label>
                <input
                  type="text"
                  value={newUserData.tempPassword}
                  onChange={(e) => setNewUserData({...newUserData, tempPassword: e.target.value})}
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px'
                  }}
                  placeholder="Min 8 chars, uppercase, lowercase, number"
                />
                <small style={{ color: '#666', fontSize: '14px' }}>
                  User will be required to change this on first login
                </small>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary"
                  style={{ flex: 1, padding: '12px', opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUserData({ email: '', parentName: '', studentName: '', parentEmail: '', phone: '', tempPassword: '' });
                  }}
                  className="btn-secondary"
                  style={{ flex: 1, padding: '12px' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Admin;

